import { useCallback, useEffect, useState } from 'react';

/**
 * Cross-platform "Add to home screen" nudge for any Kilat PWA.
 *
 * Sprint 88 — extracted from the customer-pwa-only InstallHint so
 * agent-pwa + dashboard can mount the same component. Decoupled from
 * any specific brand/tenant hook: the caller passes every string so this stays
 * usable across white-label (tenant brand) AND Kilat-branded (platform
 * brand) surfaces.
 *
 * Two paths, one component:
 *
 *   - **Android Chrome / Edge / Brave** — captures `beforeinstallprompt`,
 *     suppresses Chrome's mini-info-bar, shows our styled "Install"
 *     button. One tap → native install dialog.
 *     Accepted → `appinstalled` fires → component returns null forever.
 *     Dismissed → 30-day localStorage cooldown.
 *
 *   - **iOS Safari** — no `beforeinstallprompt`; renders a 3-step
 *     instruction card walking the user through Share → Add to Home
 *     Screen → Add. Same 30-day cooldown on "Not now".
 *
 * Returns null on:
 *   - Already in standalone mode (`display-mode: standalone` or
 *     `navigator.standalone`)
 *   - iOS Chrome / Firefox / Edge / Opera (different install flow we
 *     don't control)
 *   - Desktop without an active deferred prompt (the browser's own
 *     address-bar install UI handles it)
 *   - Within the 30-day cooldown after a previous dismissal
 *
 * Styling: theme-token-driven (CSS variables) so the same component
 * adopts the host app's palette without props. Brand-name and copy
 * are the only behavioural inputs.
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** A multiplication sign used as a close glyph — a symbol, not copy. */
const DISMISS_GLYPH = '\u00d7';

export interface PwaInstallHintProps {
  /**
   * Unique-per-app storage key for the dismiss cooldown. Different
   * apps share localStorage on `*.web.app` etc., so each PWA needs
   * its own key (e.g., `kilat.install-hint.dashboard`).
   */
  readonly storageKey: string;
  /**
   * Optional one-liner shown under the headline. Defaults to a generic
   * "Fast launch + push notifications" copy; pass your own when you
   * want a brand-specific reason ("Tap once to record a wash" etc.).
   */
  readonly subline?: string;
  /** Dismiss cooldown in days. Default 30. */
  readonly dismissDays?: number;
  /**
   * Every visible string in the card. Required, not defaulted: a default would
   * reintroduce English into a shared component and let a caller forget
   * silently. The iOS steps are whole sentences because word order differs
   * across locales — fragments stitched around an inline icon cannot translate.
   */
  readonly labels: PwaInstallHintLabels;
}

export interface PwaInstallHintLabels {
  /** Headline with the app name already interpolated by the caller. */
  readonly headline: string;
  readonly install: string;
  readonly installing: string;
  readonly notNow: string;
  readonly dismiss: string;
  readonly shareIcon: string;
  readonly iosStep1: string;
  readonly iosStep2: string;
  readonly iosStep3: string;
  /** Accessible name for the whole card, e.g. "Install Kilat Agent". */
  readonly dialogLabel: string;
}

type Platform = 'ios-safari' | 'installable' | 'unsupported';

export function PwaInstallHint(props: PwaInstallHintProps) {
  const { storageKey, subline, dismissDays = 30, labels } = props;
  const [platform, setPlatform] = useState<Platform>('unsupported');
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Capture beforeinstallprompt / appinstalled. Done at mount so we
  // never miss the event Chrome fires once it deems the page eligible.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onBeforeInstallPrompt(e: Event) {
      const evt = e as BeforeInstallPromptEvent;
      e.preventDefault();
      setDeferred(evt);
    }
    function onAppInstalled() {
      setInstalled(true);
      setDeferred(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  // Platform + dismissal check at mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone()) {
      setPlatform('unsupported');
      return;
    }
    setPlatform(isIosSafari() ? 'ios-safari' : 'installable');

    const ts = safeReadStorage(storageKey);
    if (ts) {
      const ms = Number(ts);
      if (Number.isFinite(ms) && Date.now() - ms < dismissDays * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    }
  }, [storageKey, dismissDays]);

  const recordDismiss = useCallback(() => {
    safeWriteStorage(storageKey, String(Date.now()));
    setDismissed(true);
  }, [storageKey]);

  const handleInstall = useCallback(async () => {
    if (!deferred || busy) return;
    setBusy(true);
    try {
      await deferred.prompt();
      const result = await deferred.userChoice;
      setDeferred(null);
      if (result.outcome === 'dismissed') recordDismiss();
    } catch {
      setDeferred(null);
    } finally {
      setBusy(false);
    }
  }, [deferred, busy, recordDismiss]);

  if (installed) return null;
  if (platform === 'unsupported') return null;
  if (dismissed) return null;
  if (platform === 'installable' && !deferred) return null;

  const hintLine = subline;

  return (
    <aside
      role="dialog"
      aria-label={labels.dialogLabel}
      style={{
        marginTop: 'var(--space-6)',
        padding: 'var(--space-4) var(--space-5)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface, #ffffff)',
        boxShadow: 'var(--shadow-2)',
        maxWidth: 380,
        width: '100%',
        display: 'grid',
        gap: 'var(--space-3)',
        textAlign: 'left',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-2)',
        }}
      >
        <div>
          <p style={{ margin: 0, fontWeight: 500 }}>{labels.headline}</p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted, #64748b)',
              lineHeight: 1.4,
            }}
          >
            {hintLine}
          </p>
        </div>
        <button
          type="button"
          onClick={recordDismiss}
          aria-label={labels.dismiss}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 4,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted, #64748b)',
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          {DISMISS_GLYPH}
        </button>
      </header>

      {platform === 'ios-safari' ? (
        <IosInstructions labels={labels} />
      ) : (
        <button
          type="button"
          onClick={() => void handleInstall()}
          disabled={busy}
          style={{
            background: 'var(--color-primary, #475569)',
            color: 'var(--color-primary-fg, #ffffff)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '12px 18px',
            fontSize: 'var(--text-body)',
            fontWeight: 500,
            cursor: busy ? 'wait' : 'pointer',
            boxShadow: 'var(--shadow-1)',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? labels.installing : labels.install}
        </button>
      )}

      <button
        type="button"
        onClick={recordDismiss}
        style={{
          background: 'transparent',
          color: 'var(--color-on-surface-muted, #64748b)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-sm)',
          padding: '8px 14px',
          fontSize: 'var(--text-meta)',
          cursor: 'pointer',
          justifySelf: 'end',
        }}
      >
        {labels.notNow}
      </button>
    </aside>
  );
}

function IosInstructions({ labels }: { readonly labels: PwaInstallHintLabels }) {
  return (
    <ol
      style={{
        margin: 0,
        padding: '0 0 0 var(--space-5)',
        fontSize: 'var(--text-meta)',
        color: 'var(--color-on-surface-muted, #64748b)',
        lineHeight: 1.6,
        display: 'grid',
        gap: 'var(--space-1)',
      }}
    >
      <li>
        {labels.iosStep1} <ShareIcon aria-label={labels.shareIcon} />
      </li>
      <li>{labels.iosStep2}</li>
      <li>{labels.iosStep3}</li>
    </ol>
  );
}

function ShareIcon({ 'aria-label': ariaLabel }: { readonly 'aria-label'?: string }) {
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        verticalAlign: 'text-bottom',
        margin: '0 2px',
      }}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden focusable="false">
        <path fill="currentColor" d="M12 3l-4 4h3v8h2V7h3l-4-4zm-7 12v6h14v-6h-2v4H7v-4H5z" />
      </svg>
    </span>
  );
}

// ── platform / storage helpers ────────────────────────────────────────

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIosDevice =
    /iPhone|iPod/.test(ua) ||
    /iPad/.test(ua) ||
    (navigator.platform === 'MacIntel' &&
      (navigator as { maxTouchPoints?: number }).maxTouchPoints !== undefined &&
      ((navigator as { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 1);
  if (!isIosDevice) return false;
  if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return false;
  return /Safari/.test(ua);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  const navAny = window.navigator as Navigator & { standalone?: boolean };
  return navAny.standalone === true;
}

function safeReadStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWriteStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — swallow */
  }
}
